// Import diagnostics: the one shape the API returns for anything wrong with an
// uploaded statement, plus the copy catalogue that turns it into instructions.
//
// The API returns `{ detail, diagnostics: [{ code, severity, section, remedy,
// message, details: { missing, extra, found } }] }` (apps/api/errors.py). Several
// CODES share one REMEDY -- Account Trade History missing columns, Cash Balance
// missing columns, and the ambiguous-duplicate case all resolve to "open the
// Customize dialog and click Load Defaults". So the catalogue is keyed on
// `remedy`, not `code`: that many-to-one is the entire reason the two fields are
// separate, and keying on code would mean copying the same procedure per code.

import type { components } from "../api/schema";

/**
 * Every remedy key the SERVER can send, read from the generated schema.
 *
 * Generated rather than hand-listed on purpose: this is the one list that
 * cannot silently fall behind the server, because `openapi:gen` derives it from
 * the committed snapshot and two freshness gates (a pytest for the snapshot, a
 * vitest for this file) fail if either generated artefact goes stale.
 */
export type ImportRemedy = components["schemas"]["ImportRemedy"];

export type ImportDiagnosticSeverity = "block" | "warn";

export type ImportDiagnostic = {
  code: string;
  severity: ImportDiagnosticSeverity;
  section: string;
  remedy: string;
  message: string;
  missing: string[];
  extra: string[];
  found: string[];
};

export const REMEDY_CUSTOMIZE_COLUMNS = "thinkorswim_customize_columns";
export const REMEDY_OPTIONS_UNSUPPORTED = "options_not_supported";
export const REMEDY_REBIND_ACCOUNT_MASK = "rebind_account_mask";
export const REMEDY_EXPORT_ACCOUNT_STATEMENT = "export_account_statement";
export const REMEDY_ACCOUNT_BOUND_IMPORT_FAILED = "account_bound_import_failed";
export const REMEDY_RETRY_IMPORT = "retry_import";
export const REMEDY_SET_STATEMENT_TIMEZONE = "set_statement_timezone";
export const REMEDY_ACCOUNT_NEEDS_RELOCALISATION = "account_needs_relocalisation";
export const REMEDY_STATEMENT_TIMEZONE_MISMATCH = "statement_timezone_mismatch";
export const REMEDY_STATEMENT_ALREADY_IMPORTED = "statement_already_imported";
export const REMEDY_STATEMENT_PARTIALLY_IMPORTED = "statement_partially_imported";

// The CSV spells two columns differently from the thinkorswim dialog the user
// has to open. Mirrors _UI_COLUMN_LABELS in the parser: a user scanning the
// column list for "Pos Effect" or "Exp" will not find them. Kept here because
// the API's `missing` carries the CSV's names (machine-readable, matching
// `found`); the mapping to what the user actually sees is a display concern.
const DIALOG_LABELS: Record<string, string> = {
  "Pos Effect": "Position Effect",
  Exp: "Expiration",
};

export function dialogLabel(column: string): string {
  return DIALOG_LABELS[column] ?? column;
}

export type RemedyCopy = {
  title: string;
  /** Why it matters, in the user's terms. Rendered above the steps. */
  explanation: (diagnostic: ImportDiagnostic) => string;
  /**
   * A procedure, so it renders as an ordered list. Empty when there is nothing
   * the user can do -- some diagnostics explain rather than instruct.
   */
  steps: (diagnostic: ImportDiagnostic) => string[];
  /** Reassurance, rendered last. */
  footnote?: string;
  /**
   * An action the user can take from here, rather than only in another app.
   *
   * The account-mask mismatch is the first diagnostic with an in-app fix, and
   * detection without recovery is what turns a recoverable mistake into a
   * permanent block whose only exit is support. The page supplies the handler;
   * the catalogue only names the button.
   */
  action?: { kind: "rebind-account-mask"; label: string };
};

/**
 * THE PRIMARY COVERAGE GUARD, and it is the TYPE rather than a test.
 *
 * Keyed by the GENERATED server union, so every remedy key the server can send
 * must have copy here or `pnpm typecheck` fails -- at build time, already in CI,
 * and impossible to forget the way a test can be.
 *
 * **Total as of 2026-10-09**: this was briefly
 * `Record<Exclude<ImportRemedy, PendingRemedy>, RemedyCopy>` while three keys
 * shipped from the server without copy. Those three now have entries, so the
 * exclusion and the `PendingRemedy` type are gone and the guard covers the whole
 * key list. It was never `Partial<Record<...>>`: Partial makes every key
 * optional, which would accept a brand-new server key silently and leave the
 * guard decorative.
 */
const REMEDY_COPY: Record<ImportRemedy, RemedyCopy> = {
  [REMEDY_CUSTOMIZE_COLUMNS]: {
    title: "This statement is missing columns the import needs",
    explanation: (diagnostic) => {
      const base =
        `Your thinkorswim ${diagnostic.section} is set to export ` +
        `${diagnostic.found.length} columns, and ${diagnostic.missing.length} the import ` +
        `needs are switched off.`;
      if (!diagnostic.missing.includes("Pos Effect")) return base;
      return (
        `${base} The most important missing one is Position Effect, which records ` +
        `whether a trade opened or closed a position — without it we can't tell a ` +
        `buy-to-open from a buy-to-close, so these trades can't be recorded correctly.`
      );
    },
    steps: (diagnostic) => [
      "In thinkorswim, open the Monitor tab → Account Statement.",
      `On the ${diagnostic.section} section, open its column settings (the gear icon in ` +
        "the section header, or right-click the column headers → Customize).",
      "Click Load Defaults at the bottom of the dialog, then OK.",
      "Export the statement again and upload the new file.",
    ],
    footnote:
      "No trades are lost. Everything is still in thinkorswim — it just wasn't " +
      "included in this export.",
  },
  // No steps: there is no export setting the user can change, and no other route
  // into the app that would work either (manual entry hits the same multiplier
  // gap). Explaining is the honest thing to do; instructing would be a lie.
  [REMEDY_OPTIONS_UNSUPPORTED]: {
    title: "This statement contains options trades, which we can't import yet",
    explanation: () =>
      "We can't record an options position correctly today — it would be filed " +
      "against the underlying stock rather than the contract, at the wrong size " +
      "and with no fees, so your realized P&L would be wrong. " +
      "The whole statement is refused, including its stock trades, even if only " +
      "one trade in the file is an option: importing just the stock trades would " +
      "leave a journal that looks complete while the period's P&L is still wrong, " +
      "because those options positions existed either way.",
    steps: () => [],
    footnote:
      "Nothing is wrong with your export and there's no setting to change. " +
      "Options support is being worked on separately. A statement with only stock " +
      "trades will import normally in the meantime.",
  },
  // The one diagnostic with an in-app remedy. Two routes out, and the copy has
  // to offer both: file it into the right account, or -- if this account really
  // is the statement's -- re-link it. The second is only correct when the
  // binding itself is wrong (a mis-filed FIRST import, or a broker renumbering),
  // so it is phrased as a condition the user checks rather than a next step.
  [REMEDY_REBIND_ACCOUNT_MASK]: {
    title: "This statement belongs to a different broker account",
    explanation: (diagnostic) => {
      const statement = diagnostic.found[0] ?? "another account";
      const bound = diagnostic.extra[0] ?? "a different account";
      return (
        `The file is for broker account ${statement}, but the account you picked is ` +
        `linked to ${bound}. Nothing was imported — the trades themselves are fine, ` +
        `they would just have been filed in the wrong place, which is the one mistake ` +
        `that is invisible afterwards.`
      );
    },
    steps: (diagnostic) => {
      const statement = diagnostic.found[0] ?? "the statement's account";
      return [
        "If you picked the wrong account, choose the right one above and import again.",
        `If this account really is ${statement} — the first import was filed wrongly, ` +
          "or your broker renumbered it — re-link it below and import again.",
      ];
    },
    footnote:
      "Re-linking only changes which statements this account accepts from now on. " +
      "Trades already imported stay exactly where they are.",
    action: { kind: "rebind-account-mask", label: "Re-link this account" },
  },
  // Not an error to fix -- a state to disclose. The link is saved separately
  // from the trades, so it survives an import that failed for its own reasons,
  // and a user reading "nothing was imported" would otherwise never learn the
  // account is now committed to one broker account.
  [REMEDY_ACCOUNT_BOUND_IMPORT_FAILED]: {
    title: "Nothing was imported, but this account is now linked",
    explanation: (diagnostic) => {
      const statement = diagnostic.found[0] ?? "that broker account";
      return (
        `The import failed, so no trades were added. The link to ${statement} was ` +
        `saved before the failure though, and it stays — from now on this account ` +
        `accepts statements for ${statement} and refuses others.`
      );
    },
    steps: () => [
      "Fix whatever the import failed on, then import the same statement again.",
      "If the link itself is wrong, re-link the account below first.",
    ],
    footnote:
      "Re-importing is safe: fills are matched on their broker ids, so nothing " +
      "will be duplicated.",
    action: { kind: "rebind-account-mask", label: "Re-link this account" },
  },
  // The only TRANSIENT diagnostic in the catalogue, and the only one whose
  // instruction is "do the same thing again". Database lock contention -- a lock
  // wait timeout or a deadlock -- aborts the whole transaction server-side
  // before the driver raises, so nothing was written and a retry cannot
  // double-apply anything. That safety is a property of both engines, not a
  // hope, which is why the copy can tell the user to simply try again.
  //
  // It deliberately does NOT name the account. A deadlock on the shared
  // `instruments` table can be caused by an import of a DIFFERENT account in the
  // same tenant, so "another import of this account is running" would state
  // something false with confidence.
  //
  // Owed from the server-side half (23636d1), which shipped the classification
  // and the 503. Until now the catalogue had no entry, so the page fell back to
  // the diagnostic's own `message` -- correct, but generic, and with no "try
  // again" framing.
  [REMEDY_RETRY_IMPORT]: {
    title: "Another import was writing at the same time",
    explanation: () =>
      "Nothing was imported, and nothing was half-imported — the database stopped " +
      "the whole thing rather than let two writes collide. This clears on its own.",
    steps: () => [
      "Wait a few seconds.",
      "Import the same file again.",
    ],
    footnote:
      "Re-importing is safe: fills are matched on their broker ids, so nothing " +
      "will be duplicated.",
  },
  // Reached by the re-link upload, where knowing the account IS the request --
  // so an unreadable header is a refusal rather than something to work around.
  [REMEDY_EXPORT_ACCOUNT_STATEMENT]: {
    title: "This file doesn't say which broker account it's for",
    explanation: () =>
      "Re-linking an account needs a statement that names the broker account, and " +
      "this file has no “Account Statement for …” line. It may be a " +
      "single-grid export (just the trade table) rather than a full Account Statement.",
    steps: () => [
      "In thinkorswim, open the Monitor tab → Account Statement.",
      "Export the whole statement, not an individual section.",
      "Upload that file instead.",
    ],
  },

  // ADR 0039 D9. A statement prints wall clocks without saying which zone they
  // are in, so the app refuses rather than guessing -- the old guess
  // (America/Chicago) put every live fill two hours early. The copy asks the one
  // question the user can actually answer: what their platform DISPLAYS.
  //
  // It suggests no zone, deliberately. Suggesting one is how a wrong guess
  // becomes a confirmed wrong guess, and the zone locks on the first import.
  [REMEDY_SET_STATEMENT_TIMEZONE]: {
    title: "This account needs to know what timezone your statements are in",
    explanation: () =>
      "A thinkorswim statement prints execution times as a plain wall clock — " +
      "“10:02:36” — without saying which timezone that is. Until you say, the app " +
      "can't tell what those times mean, so it won't import them and risk filing " +
      "every trade at the wrong time of day.",
    steps: () => [
      "Choose the timezone your thinkorswim is set to display.",
      "Check the sample time shown against what you remember seeing in thinkorswim.",
      "Import the statement.",
    ],
    footnote:
      "You can change this until you import your first statement. After that it " +
      "becomes part of how every imported trade is identified, so it's fixed.",
  },

  // ADR 0039 D9.2's lock. A LEGACY account: imported fills but no zone, so its
  // stored instants are on the old reading.
  //
  // NO STEPS AND NO ACTION, and that is the entry's whole point. Choosing a zone
  // here would change what the existing fills MEAN without moving them, so a
  // picker that appeared to work would be worse than none. `steps: () => []`
  // matches the options-unsupported entry: when there is nothing the user can
  // do, saying so plainly beats inventing a procedure.
  [REMEDY_ACCOUNT_NEEDS_RELOCALISATION]: {
    title: "This account's existing trade times need correcting first",
    explanation: () =>
      "This account has trades that were imported before the app knew which " +
      "timezone your statements are in, so their recorded times need correcting " +
      "before anything else is imported into it. Setting a timezone now would " +
      "change what those existing trades mean without moving them, which would " +
      "make the problem harder to see rather than fixing it.",
    steps: () => [],
    footnote:
      "This isn't something you can set yourself, and nothing you did caused it. " +
      "Importing is paused for this account until the correction has been applied.",
  },

  // ADR 0039 D9.9 -- the zone moved between this import resolving a zone and
  // writing its fills. `_reassert_locked` re-reads under the account row lock
  // and refuses, so ZERO fills land.
  //
  // RE-VALIDATION IS STEP 1, and it is the part that matters: the preview the
  // user is looking at was computed in the OLD zone, so retrying straight from
  // it would be reasoning from a stale screen. The copy names BOTH zones --
  // `found` is what the import asked for, `extra` is what the account now uses
  // (build_timezone_mismatch_diagnostic) -- because "wrong timezone" alone is
  // unactionable.
  [REMEDY_STATEMENT_TIMEZONE_MISMATCH]: {
    title: "The timezone changed while this import was running",
    explanation: (diagnostic) => {
      const askedFor = diagnostic.found[0];
      const nowSet = diagnostic.extra[0];
      const base = "Nothing was imported.";
      if (askedFor === undefined || nowSet === undefined) {
        // Both zones come off the same diagnostic, so this should not happen --
        // but a generic sentence beats "undefined" on screen if the server ever
        // sends the key without them.
        return (
          `${base} This account's statement timezone changed while the import was ` +
          `running, so it stopped rather than file your trades under a timezone ` +
          `the account no longer uses.`
        );
      }
      return (
        `${base} The statement's times were being read as ${askedFor}, and this ` +
        `account is now set to ${nowSet}, so the import stopped rather than file ` +
        `your trades under a timezone the account no longer uses.`
      );
    },
    steps: () => [
      "Upload the statement again to re-check it against the new timezone.",
      "Import it.",
    ],
    footnote:
      "Re-checking first matters: the preview you were looking at was worked out " +
      "using the old timezone, so the times on it are no longer the ones that " +
      "would be imported.",
  },

  // ADR 0039 D9.6 -- the stale-`external_id` guard, FULL-OVERLAP arm. Every fill
  // in the file is already in the journal, so there is nothing to recover and
  // nothing to do: reassure, and stop.
  //
  // NO STEPS, for the same reason as the options and needs-relocalisation
  // entries: remap-on-re-upload (D9.7) is a later unit, so there is no procedure
  // that would work, and inventing one would send the user round a loop that can
  // only refuse again.
  //
  // "Nothing is missing" is TRUE here and is why this key is separate from the
  // partial one below. The server decides which it sends by counting the fills
  // that are NOT already stored; it never sends this arm when that count is
  // non-zero.
  [REMEDY_STATEMENT_ALREADY_IMPORTED]: {
    title: "These trades are already in your journal",
    explanation: () =>
      "The execution times on this account were corrected, so the rows in this " +
      "file no longer line up with the ones already stored. Importing it would " +
      "create a second copy of every trade rather than recognising them, so it " +
      "was stopped.",
    steps: () => [],
    footnote:
      "Nothing is missing and nothing went wrong — you don't need to upload this " +
      "file. Newer statements will import normally.",
  },

  // The PARTIAL-OVERLAP arm, and the distinction is not cosmetic. Here the file
  // holds an already-imported period PLUS newer trades, and those newer trades
  // genuinely did NOT land -- so the full-overlap copy above would tell the user
  // they lost nothing when they did. That is the one thing this pair of keys
  // exists to prevent.
  //
  // Counts only: the server sends both (`already` / `not imported`) and no time
  // from the statement, because a statement's own clock faces never leave the
  // user's own screen (D9.2).
  [REMEDY_STATEMENT_PARTIALLY_IMPORTED]: {
    title: "Some of these trades are already in your journal",
    explanation: () =>
      "The execution times on this account were corrected, so part of this file " +
      "no longer lines up with what is already stored. Importing it would create " +
      "a second copy of those trades, so the whole file was stopped — which means " +
      "the newer trades in it have NOT been imported yet.",
    steps: () => [
      "In thinkorswim, open the Monitor tab → Account Statement.",
      "Set the date range to start AFTER your last imported trade.",
      "Export that statement and upload it instead.",
    ],
    footnote:
      "Nothing was duplicated and nothing already imported was touched. The server " +
      "message above says how many fills in this file are already stored and how " +
      "many are still to come in.",
  },
};

export function remedyCopyFor(remedy: string): RemedyCopy | undefined {
  // THE PARAMETER STAYS `string`, deliberately, and this is not an oversight.
  // The value arrives from the network, so a newer server can send a key this
  // cached bundle has never heard of -- something no compile-time guard can
  // reach. Narrowing the parameter to `ImportRemedy` would make the unknown-key
  // call unexpressible and so delete the runtime fallback by accident, while
  // appearing to tighten types.
  //
  // The RECORD is keyed by the union -- that is where the guard lives, and a
  // missing key is a compile error at the literal above. The LOOKUP accepts
  // anything and may return undefined, which is what the caller handles.
  return (REMEDY_COPY as Record<string, RemedyCopy | undefined>)[remedy];
}

/**
 * Whether the import must be blocked.
 *
 * Derived from the diagnostics list itself -- never from a separate flag the API
 * sets. One source of truth, or the two drift exactly the way the deploy gate
 * did when it kept its own copy of the column rule.
 */
export function hasBlockingDiagnostic(diagnostics: readonly ImportDiagnostic[]): boolean {
  return diagnostics.some((diagnostic) => diagnostic.severity === "block");
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

/**
 * Lift diagnostics out of an error body, defensively.
 *
 * Anything malformed yields [] rather than throwing, so a shape change on the
 * server degrades to the plain `detail` message instead of breaking the page.
 */
export function parseImportDiagnostics(body: unknown): ImportDiagnostic[] {
  if (typeof body !== "object" || body === null || !("diagnostics" in body)) return [];
  const raw = (body as { diagnostics?: unknown }).diagnostics;
  if (!Array.isArray(raw)) return [];

  const parsed: ImportDiagnostic[] = [];
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue;
    const record = entry as Record<string, unknown>;
    const severity = record.severity === "warn" ? "warn" : "block";
    const details =
      typeof record.details === "object" && record.details !== null
        ? (record.details as Record<string, unknown>)
        : {};
    parsed.push({
      code: typeof record.code === "string" ? record.code : "unknown",
      severity,
      section: typeof record.section === "string" ? record.section : "",
      remedy: typeof record.remedy === "string" ? record.remedy : "",
      message: typeof record.message === "string" ? record.message : "",
      missing: asStringArray(details.missing),
      extra: asStringArray(details.extra),
      found: asStringArray(details.found),
    });
  }
  return parsed;
}
